/**
 * Confirmación de procesar UNA jornada, y su resultado.
 *
 * Lo que se confirma no es "procesar": es escribir en GeoVictoria, y de ahí
 * pasa a la liquidación. Así que el diálogo dice, con nombre y apellido, qué
 * se va a registrar y sobre quién — no alcanza con preguntar "¿estás seguro?",
 * que es una pregunta que nadie lee.
 *
 * El diálogo NO se cierra al confirmar: se queda mostrando que está
 * trabajando y después qué pasó con cada acción, y recién ahí ofrece cerrar
 * (pedido del 15-09). Cerrarlo de entrada dejaba al operador mirando una
 * grilla sin saber si lo suyo había entrado.
 *
 * Es el equivalente de a uno del diálogo del lote (`DialogoMasivo`).
 */

import { AlertTriangle, Check, Loader2, X } from 'lucide-react';

import type { JornadaDto } from '../../api';
import { Modal } from '../../components/Modal';
import { aDdMmAaaa } from '../../lib/fechas';
import { SalidaAMarcar } from './SalidaAMarcar';

/** Cómo salió cada acción, para mostrarla con su ícono. */
export interface LineaResultado {
  readonly texto: string;
  readonly estado: 'ok' | 'yaEstaba' | 'error';
}

export interface ResultadoProceso {
  readonly huboError: boolean;
  readonly lineas: readonly LineaResultado[];
}

export interface PropsDialogoProcesar {
  /** La jornada a procesar, o `null` si no hay ninguna pendiente. */
  readonly jornada: JornadaDto | null;
  readonly procesando: boolean;
  /** `null` mientras no terminó; con valor, lo que pasó. */
  readonly resultado: ResultadoProceso | null;
  readonly onCancelar: () => void;
  readonly onConfirmar: () => void;
  readonly onCerrar: () => void;
}

/** Texto de cada acción, en lo que significa y no en su identificador. */
const QUE_HACE: Readonly<Record<string, string>> = {
  LICENCIA_ASISTENCIA_VIAJE: 'Registrar la licencia ASISTENCIA AL VIAJE',
  LICENCIA_SIN_TAREA: 'Registrar la licencia SIN TAREA',
  SALIDA_ARTIFICIAL: 'Marcar la salida al fin del turno',
};

export function DialogoProcesar({
  jornada: j,
  procesando,
  resultado,
  onCancelar,
  onConfirmar,
  onCerrar,
}: PropsDialogoProcesar): React.JSX.Element {
  const acciones = j?.accionesRequeridas ?? [];
  const terminado = resultado !== null;

  return (
    <Modal
      abierto={j !== null}
      titulo={terminado ? 'Resultado del registro' : 'Confirmar el registro en GeoVictoria'}
      // Mientras escribe no se cierra: ni con Escape, ni con el fondo, ni con
      // la cruz. Cerrarlo no cancelaría nada —el pedido ya salió— y dejaría al
      // operador sin saber cómo terminó.
      onCerrar={procesando ? () => undefined : terminado ? onCerrar : onCancelar}
      pie={
        terminado ? (
          <button type="button" onClick={onCerrar}>
            Cerrar
          </button>
        ) : (
          <>
            <button type="button" className="plano" onClick={onCancelar} disabled={procesando}>
              Cancelar
            </button>
            <button type="button" onClick={onConfirmar} disabled={procesando}>
              {procesando && <Loader2 className="girando" size={15} aria-hidden="true" />}
              {procesando ? 'Registrando…' : 'Registrar'}
            </button>
          </>
        )
      }
    >
      {j && (
        <>
          <p>
            {terminado
              ? `Sobre ${j.nombre ?? `el dni ${j.dni}`} (dni ${j.dni}), jornada del ${aDdMmAaaa(j.fechaOperativa)}:`
              : `Sobre ${j.nombre ?? `el dni ${j.dni}`} (dni ${j.dni}), jornada del ${aDdMmAaaa(j.fechaOperativa)}, se va a:`}
          </p>

          {terminado ? (
            <ul className="lista-resultado">
              {resultado.lineas.map((l) => (
                <li key={l.texto} data-estado={l.estado}>
                  {l.estado === 'error' ? (
                    <X size={15} aria-hidden="true" />
                  ) : (
                    <Check size={15} aria-hidden="true" />
                  )}
                  {l.texto}
                </li>
              ))}
            </ul>
          ) : (
            <ol className="lista-acciones">
              {acciones.map((a) => (
                <li key={a}>
                  {QUE_HACE[a] ?? a}
                  {a === 'SALIDA_ARTIFICIAL' && (
                    <>
                      {': '}
                      <SalidaAMarcar jornada={j} />
                    </>
                  )}
                </li>
              ))}
            </ol>
          )}

          {/*
            Con fichada de salida real no se marca nada (§12.4). Decirlo acá
            evita la pregunta de por qué la lista tiene un solo renglón.
          */}
          {!terminado && j.fichadaSalida && (
            <p className="nota">
              Ya fichó salida, así que no se marca ninguna: la jornada está cerrada con la
              marcación real.
            </p>
          )}

          {procesando && (
            <p className="nota">
              Registrando en GeoVictoria. No cierres esta ventana hasta que termine.
            </p>
          )}

          {!terminado && (
            <p className="aviso aviso--atencion">
              <AlertTriangle className="aviso-icono" size={16} aria-hidden="true" />
              <span className="aviso-texto">
                Lo que se registre acá se replica al módulo Payroll de Visma. Una licencia mal
                puesta se corrige a mano en GeoVictoria: no hay forma de darla de baja desde el
                portal.
              </span>
            </p>
          )}
        </>
      )}
    </Modal>
  );
}
